import { motion } from "framer-motion";
import { ArrowUpRight, Wrench, Gauge, Building2, Droplets, ShieldCheck, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICE_GROUPS } from "../data/site";
import { Reveal, Eyebrow } from "./Reveal";

const CATEGORY_ICONS = {
  "Mobile Maintenance": Wrench,
  "PM Services": Gauge,
  "Shop Services": Building2,
  "Industrial Cleaning": Droplets,
  "Inspections": ShieldCheck,
  "Fleet Management": BarChart3,
};

export default function Services() {
  return (
    <section id="services" className="bg-mm-base py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-20">
          <div className="max-w-3xl">
            <Reveal><Eyebrow>What We Do</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading font-black uppercase tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
                Total Fleet <span className="text-mm-green">Maintenance</span> Solutions
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-gray-400 lg:text-right">
              One partner for every asset in your fleet, mobile, in-shop, preventative,
              and everything in between.
            </p>
          </Reveal>
        </div>

        {/* Categories */}
        <div className="space-y-24">
          {SERVICE_GROUPS.map((group, gi) => {
            const Icon = CATEGORY_ICONS[group.category];
            return (
              <div key={group.category} data-testid={`service-group-${group.category.toLowerCase().replace(/\s+/g, "-")}`}>
                {/* Category header row */}
                <div className="grid grid-cols-12 gap-6 items-end border-b border-mm-green/20 pb-6 mb-10">
                  <Reveal className="col-span-12 lg:col-span-1">
                    <div className="p-3 border border-mm-green/30 text-mm-green inline-block">
                      {Icon && <Icon size={22} strokeWidth={1.6} />}
                    </div>
                  </Reveal>
                  <Reveal delay={0.05} className="col-span-12 lg:col-span-7">
                    <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-mm-green mb-2">
                      {String(gi + 1).padStart(2, "0")} · {group.tag}
                    </div>
                    <h3 className="font-heading font-black uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                      {group.category}
                    </h3>
                  </Reveal>
                  <Reveal delay={0.1} className="col-span-12 lg:col-span-4 lg:text-right">
                    <span className="font-mono text-xs uppercase tracking-widest text-gray-500">
                      {group.items.length} {group.items.length === 1 ? "Service" : "Services"}
                    </span>
                  </Reveal>
                </div>

                {/* Sub-services grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-mm-green/15 border border-mm-green/15">
                  {group.items.map((item, i) => (
                    <motion.div
                      key={item.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                    >
                      <Link
                        to={`/services/${item.slug}`}
                        data-testid={`service-link-${item.slug}`}
                        className="group relative block h-full bg-mm-surface hover:bg-mm-panel transition-colors duration-300 p-7 min-h-[220px]"
                      >
                        <div className="flex items-start justify-between mb-8">
                          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mm-green/80">
                            0{i + 1}
                          </div>
                          <ArrowUpRight
                            size={20}
                            className="text-gray-600 group-hover:text-mm-green group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300"
                          />
                        </div>
                        <h4 className="font-heading font-bold uppercase text-xl sm:text-2xl text-white tracking-tight leading-tight group-hover:text-mm-green transition-colors">
                          {item.title}
                        </h4>
                        <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-3">
                          {item.desc}
                        </p>
                        <span className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 group-hover:text-mm-green transition-colors">
                          Read Details
                          <span className="w-6 h-px bg-current" />
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                  {/* Fill empty tail cells so the grid never shows a hollow gap */}
                  {Array.from({ length: (3 - (group.items.length % 3)) % 3 }).map((_, k) => (
                    <div
                      key={`filler-${group.category}-${k}`}
                      aria-hidden="true"
                      className="hidden lg:block bg-mm-surface min-h-[220px]"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
